import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import createMatch from "core/src/core/match.js";
import type { Match, MatchConfig, Player } from "../../shared/types";
import { keyPairs } from "../../shared/key-util.js";
import { getLocalPlayer } from "../../shared/local-players";
import { DEFAULT_MAP_VALUE, createMapFromValue } from "../setup/maps";
import { setBoardSkinPreview } from "../../game-canvas/board-skins";
import * as gsf from "core/src/core/game-state-functions.js";
import { setDebugNoCrash } from "core/src/core/core-functions.js";
import LocalGame from "../play/game/LocalGame";

// "Try it before you buy it": a solo local match with the cosmetic applied,
// launched from the shop's purchase confirmation. One worm (yours), no
// opponents, no power-ups, and it just restarts when you crash. Leaving (the
// toolbar's exit, or Escape) returns to the shop with the confirmation open.
//   /shop/try/head/<id>     the worm wears that head
//   /shop/try/board/<id>    the arena wears that skin (viewer-local override)
//   /shop/try/victory/<id>  the round is a victory lap from the first second,
//                           so the celebration plays at your head as you drive
export default function ShopTryPage() {
  const { kind, id } = useParams<{ kind: string; id: string }>();
  const navigate = useNavigate();
  const [matchNo, setMatchNo] = useState(0);
  const [match, setMatch] = useState<Match | null>(null);

  useEffect(() => {
    if (kind === "board") setBoardSkinPreview(id || null);
    return () => setBoardSkinPreview(null);
  }, [kind, id]);

  useEffect(() => {
    const you = getLocalPlayer(0, "You");
    const player: Player = {
      id: "try_you",
      type: "human",
      name: you.name,
      colorId: you.colorId,
      left: you.left ?? keyPairs[0].left,
      right: you.right ?? keyPairs[0].right,
      flag: you.flag ?? null,
      victoryAnim: kind === "victory" ? id || null : you.victoryAnim ?? null,
      headStyle: kind === "head" ? id || null : you.headStyle ?? null,
    };
    const matchConfig: MatchConfig = {
      players: [player],
      map: createMapFromValue(DEFAULT_MAP_VALUE),
      maxScore: 9999,
      powerUpSpawnChance: 0,
    };
    setMatch(createMatch({ matchConfig }));
  }, [kind, id, matchNo]);

  const TAB: Record<string, string> = { head: "heads", board: "arena", victory: "victory", flag: "flags" };
  const back = () => navigate(`/shop?tab=${TAB[kind || ""] || "packs"}&confirm=${encodeURIComponent(id || "")}`);

  // Celebration try-out: enter the victory lap as soon as the countdown is
  // over, so the celebration plays while you can actually drive. Left alone,
  // an unsteered worm hits the wall about two seconds in and the lap ends
  // before the celebration has really started, so the try-out worm starts out
  // crash proof (core's no-crash switch, normally a debug aid) and drives
  // itself in a loop around the arena centre. The moment you take the wheel
  // the autopilot AND the crash proofing both switch off, so you can crash on
  // purpose and see the celebration's crash sequence, exactly like the other
  // try-outs. A mid-lap crash doesn't stop the sim (core keeps the lap
  // running), so the crash sequence gets a few seconds to play out before the
  // restart.
  function onGameCreated(game: any) {
    // Every try-out (re)start is a fresh match. You're here to eyeball a
    // cosmetic, not get ready: a plain 3s countdown, no roll call.
    game.gameState.startPhaseTime = 3;
    game.gameState.rollCallTime = 0; // no roll call either: straight to the countdown
    if (kind !== "victory") return;
    setDebugNoCrash(true);
    let lapEntered = false;
    let steered = false;
    const onSteer = () => { steered = true; setDebugNoCrash(false); };
    window.addEventListener("keydown", onSteer);
    window.addEventListener("pointerdown", onSteer); // touch steering bypasses the keyboard
    let restartTimer = 0;
    const timer = window.setInterval(() => {
      const gs = game.gameState;
      if (!lapEntered && !gsf.isInStartPhase(gs)) { game.enterVictoryLap(); lapEntered = true; }
      if (lapEntered && !steered) {
        const cx = gs.playArea.cols / 2, cy = gs.playArea.rows / 2;
        gsf.forEachAliveWorm(gs, (worm: any) => {
          const dx = cx - worm.centerX, dy = cy - worm.centerY, dist = Math.hypot(dx, dy);
          let diff = Math.atan2(dy, dx) - worm.direction;
          diff = Math.atan2(Math.sin(diff), Math.cos(diff));
          // far out: turn towards the centre; near it: circle gently
          gsf.setPlayerSteering(gs, worm.playerId, dist > 120 ? Math.sign(diff) || 1 : 0.7);
        });
      }
      if (!restartTimer && gsf.getAlivePlayers(gs).length === 0) {
        restartTimer = window.setTimeout(() => setMatchNo((n) => n + 1), 4000);
      }
    }, 50);
    tryCleanup.current = () => {
      window.clearInterval(timer);
      window.clearTimeout(restartTimer);
      window.removeEventListener("keydown", onSteer);
      window.removeEventListener("pointerdown", onSteer);
      setDebugNoCrash(false);
    };
  }
  const tryCleanup = React.useRef<() => void>(() => {});
  useEffect(() => () => tryCleanup.current(), [matchNo]);

  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (e.key === "Escape") back();
    }
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [kind, id]);

  if (!match) return null;
  return (
    <LocalGame
      key={matchNo}
      match={match}
      isLocal
      confirmLeave={false}
      onLeaveAction={back}
      onGameCreated={onGameCreated}
      railOverride={
        <button className="app-btn app-btn-ghost leave-preview" onClick={back}>
          <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
            <polyline points="16 17 21 12 16 7" />
            <line x1="21" y1="12" x2="9" y2="12" />
          </svg>
          Leave preview
        </button>
      }
      // Crashed: run it again after a beat so the try-out never ends on its own.
      onGameOverAction={() => { window.setTimeout(() => setMatchNo((n) => n + 1), 700); }}
    />
  );
}
